
import React from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Search, RefreshCcw, Users, RotateCcw, Circle } from "lucide-react";
import ApiStatsCard from "@/components/admin/ApiStatsCard";
import UserFilters from "@/components/admin/UserFilters";
import { ConnectionStatus, PlanFilter, ChatAccessFilter } from "@/hooks/useAdminPanelState";

interface OverviewCardsProps {
  users: any[];
  subscriptions: any[];
  searchQuery: string;
  onSearchChange: (query: string) => void;
  onRefresh: () => void;
  onSync: () => void;
  isRefreshing: boolean;
  isSyncing: boolean;
  connectionFilter: ConnectionStatus;
  planFilter: PlanFilter;
  chatAccessFilter: ChatAccessFilter;
  onConnectionFilterChange: (filter: ConnectionStatus) => void;
  onPlanFilterChange: (filter: PlanFilter) => void;
  onChatAccessFilterChange: (filter: ChatAccessFilter) => void;
  filteredCount: number;
}

const OverviewCards = ({
  users,
  subscriptions,
  searchQuery,
  onSearchChange,
  onRefresh,
  onSync,
  isRefreshing,
  isSyncing,
  connectionFilter,
  planFilter,
  chatAccessFilter,
  onConnectionFilterChange,
  onPlanFilterChange,
  onChatAccessFilterChange,
  filteredCount,
}: OverviewCardsProps) => {
  const totalUsers = users.length;
  const onlineUsers = users.filter(user => {
    // Calcular status online baseado em user_stats e last_active em tempo real
    const lastActiveDate = user.last_access ? new Date(user.last_access) : null;
    const now = new Date();
    return lastActiveDate ? (now.getTime() - lastActiveDate.getTime()) < (2 * 60 * 1000) : false;
  }).length;
  
  const totalConversations = users.reduce((sum, user) => sum + (user.conversations_count || 0), 0);
  const totalDocuments = users.reduce((sum, user) => sum + (user.documents_count || 0), 0);

  return (
    <div className="space-y-6">
      {/* Stats Cards */}
      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Total de Usuários</CardTitle>
            <Users className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{totalUsers}</div>
            <p className="text-xs text-muted-foreground">
              usuários registrados
            </p>
          </CardContent>
        </Card>
        
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Usuários Online</CardTitle>
            <Circle className="h-4 w-4 text-green-500 fill-green-500" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{onlineUsers}</div>
            <p className="text-xs text-muted-foreground">
              ativos nos últimos 2 minutos
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Total de Conversas</CardTitle>
            <RotateCcw className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{totalConversations}</div>
            <p className="text-xs text-muted-foreground">
              conversas criadas
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Total de Documentos</CardTitle>
            <RotateCcw className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{totalDocuments}</div>
            <p className="text-xs text-muted-foreground">
              documentos criados
            </p>
          </CardContent>
        </Card>
      </div>

      {/* Controls Section - 2 Columns */}
      <div className="grid gap-6 md:grid-cols-2">
        {/* System Controls */}
        <Card>
          <CardHeader>
            <CardTitle>Controles do Sistema</CardTitle>
            <CardDescription>
              Gerencie e monitore o sistema administrativo
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            {/* Search */}
            <div className="relative">
              <Search className="absolute left-2 top-2.5 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Buscar usuários por email ou nome..."
                value={searchQuery}
                onChange={(e) => onSearchChange(e.target.value)}
                className="pl-8"
              />
            </div>
            
            {/* Action Buttons */}
            <div className="flex flex-wrap gap-2">
              <Button
                onClick={onRefresh}
                disabled={isRefreshing}
                variant="outline"
                size="sm"
                className="gap-2"
              >
                <RefreshCcw className={`h-4 w-4 ${isRefreshing ? 'animate-spin' : ''}`} />
                {isRefreshing ? 'Atualizando...' : 'Atualizar'}
              </Button>
              
              <Button
                onClick={onSync}
                disabled={isSyncing}
                variant="outline"
                size="sm"
                className="gap-2"
              >
                <RotateCcw className={`h-4 w-4 ${isSyncing ? 'animate-spin' : ''}`} />
                {isSyncing ? 'Sincronizando...' : 'Sincronizar'}
              </Button>
            </div>

            {/* Separador e Filtros de Usuários */}
            <div className="border-t pt-4">
              <h4 className="text-sm font-medium mb-3">Filtros de Usuários</h4>
              <UserFilters
                connectionFilter={connectionFilter}
                planFilter={planFilter}
                chatAccessFilter={chatAccessFilter}
                onConnectionFilterChange={onConnectionFilterChange}
                onPlanFilterChange={onPlanFilterChange}
                onChatAccessFilterChange={onChatAccessFilterChange}
                totalUsers={users.length}
                filteredCount={filteredCount}
              />
            </div>
          </CardContent>
        </Card>

        {/* API Stats Card */}
        <ApiStatsCard />
      </div>

    </div>
  );
};

export default OverviewCards;
